import { createFocusRingStyles, createMotionTransition } from '../interaction';
import { ButtonBase, type ButtonBaseProps } from '@mui/material';
import { designTokens } from '../tokens';
import { cardSurfaceStyles } from './cardSurface';

export type InteractiveCardProps = Omit<ButtonBaseProps, 'disableRipple'> & {
  selected?: boolean;
};

export function InteractiveCard({
  selected = false,
  disabled = false,
  sx,
  ...buttonProps
}: InteractiveCardProps) {
  const sxOverrides = Array.isArray(sx) ? sx : sx ? [sx] : [];

  return (
    <ButtonBase
      {...buttonProps}
      disabled={disabled}
      disableRipple
      sx={[
        {
          ...cardSurfaceStyles,
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          alignItems: 'stretch',
          justifyContent: 'stretch',
          borderColor: selected ? designTokens.color.brandBorder : designTokens.color.divider,
          backgroundColor: selected ? designTokens.color.selectedNavigationSurface : designTokens.color.canvas,
          textAlign: 'left',
          transition: createMotionTransition(['border-color', 'background-color']),
          '&:hover': {
            borderColor: selected ? designTokens.color.brandBorder : designTokens.color.strongDivider,
            bgcolor: selected ? designTokens.color.selectedHoverSurface : designTokens.color.hoverSurface,
          },
          // 사진처럼 불투명한 자식도 초점 표시를 덮지 않도록 콘텐츠 위에 그립니다.
          '&::after': {
            content: '""', position: 'absolute', inset: 0,
            borderRadius: 'inherit', pointerEvents: 'none', zIndex: 1,
          },
          '&.Mui-focusVisible, &:focus-visible': { outline: 'none' },
          '&.Mui-focusVisible::after, &:focus-visible::after': createFocusRingStyles({ inset: true }),
          '&.Mui-disabled, &:disabled': {
            color: designTokens.color.primaryText,
            opacity: designTokens.button.disabledOpacity,
          },
          '@media (forced-colors: active)': {
            borderColor: selected ? 'Highlight' : 'ButtonText',
            '&:hover': { borderColor: selected ? 'Highlight' : 'ButtonText' },
            outline: selected ? '1px solid Highlight' : undefined,
            outlineOffset: -3,
            '&.Mui-disabled, &:disabled': { opacity: 1, color: 'GrayText', borderColor: 'GrayText' },
          },
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
            '&:hover': { transform: 'none' },
          },
        },
        ...sxOverrides,
      ]}
    />
  );
}
